import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Sprout, FlaskConical, ClipboardList, UserRound, LogOut, Menu, X, Bell, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useLang, LangToggle } from "@/lib/i18n";
import api from "@/lib/api";
import { connectRealtimeNotifications } from "@/lib/realtimeNotifications";

const NAV = [
  { to: "/customer", end: true, icon: LayoutDashboard, labelKey: "dashboard", testid: "nav-customer-dashboard" },
  { to: "/customer/fertilizer", icon: Sprout, labelKey: "fertilizer", testid: "nav-fertilizer" },
  { to: "/customer/pesticide", icon: FlaskConical, labelKey: "pesticide", testid: "nav-pesticide" },
  { to: "/customer/orders", icon: ClipboardList, labelKey: "myOrders", testid: "nav-orders" },
  { to: "/customer/profile", icon: UserRound, labelKey: "profile", testid: "nav-profile" },
];

export default function CustomerLayout() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationIndex, setNotificationIndex] = useState(0);

  useEffect(() => {
    let active = true;

    const playNotificationSound = async () => {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;

        const ctx = new AudioCtx();
        if (ctx.state === "suspended") {
          await ctx.resume();
        }

        const master = ctx.createGain();
        master.gain.setValueAtTime(0.0001, ctx.currentTime);
        master.gain.exponentialRampToValueAtTime(0.62, ctx.currentTime + 0.03);
        master.connect(ctx.destination);

        const tone = (frequency, start, duration, volume = 0.7) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();

          osc.type = "sine";
          osc.frequency.setValueAtTime(frequency, start);

          gain.gain.setValueAtTime(0.0001, start);
          gain.gain.exponentialRampToValueAtTime(volume, start + 0.025);
          gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);

          osc.connect(gain);
          gain.connect(master);

          osc.start(start);
          osc.stop(start + duration);
        };

        const t = ctx.currentTime;

        // Sabuj Sona signature notification:
        // rising 3-note opening + bright confirmation + soft resolving note.
        tone(659,  t,        0.28, 0.75);
        tone(784,  t + 0.18, 0.28, 0.80);
        tone(988,  t + 0.36, 0.38, 0.88);
        tone(1175, t + 0.62, 0.34, 0.82);
        tone(988,  t + 0.92, 0.48, 0.70);
        tone(1319, t + 1.28, 0.72, 0.88);

        if (navigator.vibrate) {
          navigator.vibrate([90, 45, 90, 45, 140]);
        }

        setTimeout(() => {
          try { ctx.close(); } catch (_) {}
        }, 2600);
      } catch (_) {}
    };

    const addNotification = (notification, sound = true) => {
      if (!active || !notification || notification.mode !== "in_app") return;

      setNotifications((current) => {
        const id = notification.id || notification._id;
        if (id && current.some((n) => (n.id || n._id) === id)) return current;
        return [...current, notification];
      });

      setNotificationIndex(0);

      if (sound) {
        playNotificationSound();
      }
    };

    const loadNotifications = async () => {
      try {
        const res = await api.get("/notifications/mine");
        if (!active) return;
        (res.data || []).forEach((n) => addNotification(n, false));
      } catch (_) {}
    };

    loadNotifications();

    const token = localStorage.getItem("sf_token");
    const cleanup = token
      ? connectRealtimeNotifications({
          token,
          onNotification: (notification) => addNotification(notification, true),
        })
      : () => {};

    return () => {
      active = false;
      cleanup();
    };
  }, []);

  const currentNotification =
    notifications[notificationIndex] || null;

  const closeNotification = async () => {
    if (!currentNotification) return;

    try {
      await api.post(
        `/notifications/${currentNotification.id || currentNotification._id}/read`
      );
    } catch (_) {}

    const next = notificationIndex + 1;

    if (next < notifications.length) {
      setNotificationIndex(next);
    } else {
      setNotifications([]);
      setNotificationIndex(0);
    }
  };


  const go = (item) => {
    navigate(item.to);
    setOpen(false);
  };

  const sidebar = (
    <div className="w-64 bg-[#0B192C] text-slate-300 flex flex-col h-full">
      <div className="px-5 py-5 border-b border-slate-800">
        <div className="font-display font-extrabold tracking-tight text-white">SABUJ SONA FERTILIZER</div>
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mt-0.5">{t("dealerPortal")}</div>
      </div>
      <nav className="flex-1 py-4 px-3 space-y-1">
        {NAV.map((item) => (
          <button
            key={item.to}
            data-testid={item.testid}
            onClick={() => go(item)}
            className={`w-full flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-sm font-medium transition-colors ${
              (item.end ? location.pathname === item.to : location.pathname.startsWith(item.to))
                ? "bg-slate-700 text-white"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`}
          >
            <item.icon className="h-4 w-4" /> {t(item.labelKey)}
          </button>
        ))}
      </nav>
      <div className="p-4 border-t border-slate-800">
        <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
        <div className="text-xs text-slate-400 mb-3">+91 {user?.mobile}</div>
        <LangToggle className="mb-3" />
        <button data-testid="customer-logout-btn" onClick={logout} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors">
          <LogOut className="h-4 w-4" /> {t("logout")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="hidden md:block shrink-0 sticky top-0 h-screen">{sidebar}</aside>
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-[#0B192C] h-14 flex items-center justify-between px-4">
        <div className="font-display font-bold text-white text-sm">SABUJ SONA · {t("dealerPortal")}</div>
        <div className="flex items-center gap-2">
          <LangToggle />
          <button data-testid="customer-mobile-menu-btn" onClick={() => setOpen(!open)} className="text-white p-2">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {currentNotification && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-4"
          data-testid="order-notification-alert"
        >
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-amber-200 overflow-hidden">
            <div className="bg-amber-50 px-5 py-4 border-b border-amber-200 flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <Bell className="h-5 w-5 text-amber-700" />
              </div>
              <div>
                <h2 className="font-display text-lg font-bold text-slate-900">
                  New Order / Update
                </h2>
                <p className="text-sm text-slate-600 mt-0.5">
                  You have a new order update.
                </p>
              </div>
            </div>

            <div className="p-5 space-y-3">
              <div className="text-sm text-slate-500">
                Order{" "}
                <span className="font-semibold text-slate-800">
                  {currentNotification.order_number ||
                    currentNotification.order_id ||
                    "Order Update"}
                </span>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="font-semibold text-slate-900">
                  {currentNotification.product_name || "Order Update"}
                </div>

                {(currentNotification.requested_qty != null ||
                  currentNotification.approved_qty != null) && (
                  <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-slate-500">Requested</div>
                      <div className="font-semibold text-slate-900">
                        {currentNotification.requested_qty != null
                          ? `${currentNotification.requested_qty} ${currentNotification.requested_unit || ""}`
                          : "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-500">Approved</div>
                      <div className="font-semibold text-slate-900">
                        {currentNotification.approved_qty != null
                          ? `${currentNotification.approved_qty} ${currentNotification.approved_unit || ""}`
                          : "—"}
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-3">
                  <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800">
                    {currentNotification.status ||
                      currentNotification.kind ||
                      "Update"}
                  </span>
                </div>

                {currentNotification.kind === "delivery_update" && (
                  <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-4 space-y-3">
                    <div className="text-sm font-bold text-slate-900">
                      Delivery & Dispatch
                    </div>

                    {currentNotification.delivery_date && (
                      <div className="flex justify-between gap-4 text-sm">
                        <span className="text-slate-500">Delivery Date</span>
                        <span className="font-semibold text-slate-900 text-right">
                          {currentNotification.delivery_date}
                        </span>
                      </div>
                    )}

                    {currentNotification.vehicle_number && (
                      <div className="flex justify-between gap-4 text-sm">
                        <span className="text-slate-500">Vehicle Number</span>
                        <span className="font-semibold text-slate-900 text-right">
                          {currentNotification.vehicle_number}
                        </span>
                      </div>
                    )}

                    {currentNotification.driver_name && (
                      <div className="flex justify-between gap-4 text-sm">
                        <span className="text-slate-500">Driver Name</span>
                        <span className="font-semibold text-slate-900 text-right">
                          {currentNotification.driver_name}
                        </span>
                      </div>
                    )}

                    {currentNotification.driver_phone && (
                      <div className="flex justify-between gap-4 text-sm">
                        <span className="text-slate-500">Driver Phone</span>
                        <a
                          href={"tel:" + currentNotification.driver_phone}
                          className="font-semibold text-blue-600 text-right"
                        >
                          {currentNotification.driver_phone}
                        </a>
                      </div>
                    )}

                    {currentNotification.dispatch_status && (
                      <div className="flex justify-between gap-4 text-sm">
                        <span className="text-slate-500">Dispatch Status</span>
                        <span className="font-semibold text-slate-900 text-right">
                          {currentNotification.dispatch_status}
                        </span>
                      </div>
                    )}

                    {currentNotification.admin_note && (
                      <div className="pt-2 border-t border-amber-200">
                        <div className="text-xs font-semibold text-slate-500 mb-1">
                          Notice
                        </div>
                        <div className="text-sm text-slate-700">
                          {currentNotification.admin_note}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {currentNotification.kind !== "delivery_update" && (
                  <div className="mt-3 text-sm text-slate-600">
                    {currentNotification.admin_note ||
                      currentNotification.message}
                  </div>
                )}
              </div>

              <p className="text-sm font-medium text-slate-700">
                Please check your dealer portal for more details.
              </p>

              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <button
                  type="button"
                  onClick={async () => {
                    await closeNotification();
                    navigate("/customer/orders");
                  }}
                  className="sf-btn-primary flex-1 justify-center inline-flex items-center gap-2"
                >
                  <ArrowRight className="h-4 w-4" />
                  View Order
                </button>

                <button
                  type="button"
                  onClick={closeNotification}
                  className="sf-btn-outline flex-1 justify-center inline-flex items-center gap-2"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="md:hidden fixed inset-0 z-30 pt-14">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative h-full">{sidebar}</div>
        </div>
      )}
      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 pt-20 md:pt-6 lg:pt-8">
        <Outlet />
      </main>
    </div>
  );
}
